import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, FileText, Table, Users, Target, Play, Download, CheckCircle, Info,
  Check, X, AlertCircle, ChevronRight, Settings, TrendingUp, Link2, ArrowRight,
  Filter, Plus, Edit2, Trash2, Eye, Send, Calendar, RefreshCw, AlertTriangle,
  Lock, Search, Zap, Activity, Database
} from 'lucide-react';
import { apiCall, getApiUrl } from './Api';

import EnhancedFilesTab from './EnhancedFilesTab';
import { MappingEditorModal, EnhancedUnifyTab } from './EnhancedUnifyTab';
import ColumnMatchingWarning from './ColumnMatchingWarning';
import CDPAnalyticsTab from './CDPAnalyticsTab';
import APISourcesTab from './APISourcesTab';
import QueryExplorer from './QueryExplorer';
import EntityPermissionManager from './EntityPermissionManager';
import CampaignDashboard from './CampaignDashboard';
import { getCurrentToken } from './SessionManager';
import CDPInsightsTab from './CDPInsightsTab';
import CDPEventAnalyticsTab from './CDPEventAnalyticsTab';
import CampaignsTab from './CampaignsTab';
import { PageLayout, PageHeader, PageBody } from './PageLayout';


// Utility functions for data type formatting
const formatDataType = (dtype) => {
  const typeMap = {
    'int64': 'Integer',
    'int32': 'Integer',
    'int': 'Integer',
    'float64': 'Decimal',
    'float32': 'Decimal',
    'float': 'Decimal',
    'string': 'Text',
    'object': 'Text',
    'datetime': 'Date/Time',
    'datetime64': 'Date/Time',
    'bool': 'Boolean'
  };
  return typeMap[dtype] || dtype;
};

const getTypeColor = (dtype) => {
  const colorMap = {
    'int64': 'text-blue-600',
    'int32': 'text-blue-600',
    'int': 'text-blue-600',
    'float64': 'text-purple-600',
    'float32': 'text-purple-600',
    'float': 'text-purple-600',
    'string': 'text-gray-600',
    'object': 'text-gray-600',
    'datetime': 'text-green-600',
    'datetime64': 'text-green-600',
    'bool': 'text-orange-600'
  };
  return colorMap[dtype] || 'text-gray-600';
};

const CDPSessionView = ({ user, sessionId, onNavigate }) => {
  const [session, setSession] = useState(null);
  const [files, setFiles] = useState([]);
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('files');
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [columnAnalysis, setColumnAnalysis] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingLog, setProcessingLog] = useState([]);
  const ws = useRef(null);

  useEffect(() => {
    loadSessionData();
    connectWebSocket();
    
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [sessionId]);

  const connectWebSocket = () => {
    const wsUrl = `ws://localhost:8000/api/cdp/ws/${sessionId}`;
    ws.current = new WebSocket(wsUrl);
    
    ws.current.onopen = () => {
      console.log('WebSocket connected');
    };
    
    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };
    
    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
      setTimeout(connectWebSocket, 3000);
    };
  };

  const handleWebSocketMessage = (data) => {
    // Track processing status in the consolidated monitor
    setProcessingStatus(data);
    setProcessingLog(prev => [...prev, {
      timestamp: new Date(),
      message: data.message,
      type: data.type
    }].slice(-20));
    
    if (data.type === 'unification_complete' || data.type === 'segment_created' || data.type === 'unification_completed') {
      loadSessionData();
      setTimeout(() => {
        setProcessingStatus(null);
        setProcessingLog([]);
      }, 3000);
    }
  };

  const loadSessionData = async () => {
    try {
      setLoading(true);
      const token = getCurrentToken();
      
      // Load session details, files, segments, and campaigns
      const [sessionRes, filesRes, segmentsRes] = await Promise.all([
        apiCall(`/api/cdp/sessions/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        apiCall(`/api/cdp/sessions/${sessionId}/files`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        apiCall(`/api/cdp/sessions/${sessionId}/segments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (sessionRes.ok) {
        const sessionData = await sessionRes.json();
        setSession(sessionData);
      }

      if (filesRes.ok) {
        const filesData = await filesRes.json();
        setFiles(filesData.files || []);
      }

      if (segmentsRes.ok) {
        const segmentsData = await segmentsRes.json();
        setSegments(segmentsData.segments || []);
      }
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirmation = () => {
    const result = window.confirm(
      `Delete session "${session?.name}"?\n\nClick OK to delete AND clear all tables/views.\nClick Cancel to only mark as deleted.`
    );
    
    if (result !== null) {
      handleDeleteSession(result);
    }
  };

  const handleDeleteSession = async (clearTables) => {
    try {
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${sessionId}?clear_tables=${clearTables}`, 
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        if (onNavigate) {
          onNavigate('cdp');
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'files':
        return <EnhancedFilesTab session={session} files={files} onRefresh={loadSessionData} />;
      case 'api-sources':
        return <APISourcesTab session={session} onRefresh={loadSessionData} />;
      case 'unify':
        return <EnhancedUnifyTab session={session} files={files} onRefresh={loadSessionData} />;
      case 'query':
        return <QueryExplorer session={session} onSegmentSaved={() => { loadSessionData(); }} />;
      case 'segments':
        return <SegmentsTab session={session} segments={segments} onRefresh={loadSessionData} onSwitchToQueryExplorer={() => setActiveTab('query')} />;
      case 'campaigns':
        return <CampaignsTab session={session} user={user} />;
      case 'analytics':
        return <CDPAnalyticsTab sessionId={sessionId} currentVersion={session?.current_version || 1} user={user} />;
      case 'insights':
        return <CDPInsightsTab sessionId={sessionId} currentVersion={session?.current_version || 1} user={user} />;
      case 'events':
        return <CDPEventAnalyticsTab sessionId={sessionId} user={user} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <PageLayout>
      
      {/* Processing Status Monitor */}
      {processingStatus && (
        <div className="fixed bottom-4 right-4 z-50 bg-white border rounded-lg shadow-lg p-4 w-96 max-h-80 overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Processing Status</h3>
            <button 
              onClick={() => setProcessingStatus(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center space-x-2 mb-4">
            <RefreshCw className={`w-5 h-5 ${
              processingStatus.type && processingStatus.type.includes('error') ? 'text-red-500' : 
              processingStatus.type && processingStatus.type.includes('completed') ? 'text-green-500' : 
              'text-blue-500 animate-spin'
            }`} />
            <span className="font-medium text-gray-700">{processingStatus.message}</span>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
            <div className="space-y-2">
              {processingLog.map((item, idx) => (
                <div key={idx} className="text-xs text-gray-600 flex items-start space-x-2">
                  <span className="text-gray-400 whitespace-nowrap">
                    {item.timestamp.toLocaleTimeString()}
                  </span>
                  <span className="flex-1">{item.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      <PageHeader
        title={session?.name || 'CDP Session'}
        icon={Database}
        actions={
          <>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(session?.status)}`}>
              {session?.status}
            </span>
            <button
              onClick={() => setPermissionsOpen(true)}
              className="flex items-center space-x-1 px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm"
              title="Share / Manage Access"
            >
              <Users className="h-4 w-4" />
              <span>Share</span>
            </button>
            <button
              onClick={() => showDeleteConfirmation()}
              className="flex items-center space-x-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
              title="Delete CDP Session"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete Session</span>
            </button>
          </>
        }
      />
      <PageBody padded={false} className="p-6">
      {/* Session meta + tabs */}
      <div className="flex items-center flex-wrap gap-2 mb-4">
        {session?.merge_strategy && (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            {session.merge_strategy}
          </span>
        )}
        <span className="flex items-center space-x-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
          <Lock className="h-4 w-4" />
          <span>Locked</span>
        </span>
      </div>
      {/* Tabs */}
      <div className="flex items-center space-x-1 mb-6 border-b overflow-x-auto">
        {[
          { id: 'files', label: 'Files', icon: FileText },
          { id: 'api-sources', label: 'API Sources', icon: Zap },
          { id: 'unify', label: 'Data Unification', icon: Table },
          { id: 'query', label: 'Query Explorer', icon: Search },
          { id: 'segments', label: 'Segments', icon: Filter },
          { id: 'campaigns', label: 'Campaigns', icon: Target },
          { id: 'analytics', label: 'Analytics', icon: TrendingUp },
          { id: 'insights', label: 'Insights', icon: Zap },
          { id: 'events', label: 'Event Analytics', icon: Activity },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            disabled={(['analytics', 'query', 'insights', 'events'].includes(tab.id) && !session?.unified_table_name) || !session?.current_version}
            className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="h-5 w-5" />
            <span className="font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="px-6 py-6">
        {renderTabContent()}
      </div>

      {/* Entity Permission Manager */}
      <EntityPermissionManager
        entityType="cdp_session"
        entityId={sessionId}
        entityName={session?.name}
        isOpen={permissionsOpen}
        onClose={() => setPermissionsOpen(false)}
      />
    </PageBody>
    </PageLayout>
  );
};

// Files Tab Component
const FilesTab = ({ session, files, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadQueue, setUploadQueue] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const maxConcurrentUploads = 3;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const uploadSingleFile = async (file) => {
    const token = getCurrentToken();
    
    try {
      setUploadProgress(prev => ({
        ...prev,
        [file.name]: { status: 'uploading', progress: 0, error: null }
      }));

      const formData = new FormData();
      formData.append('file', file);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { ...prev[file.name], progress: Math.round(percentComplete) }
            }));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { status: 'completed', progress: 100, error: null }
            }));
            resolve();
          } else {
            const error = `Upload failed: ${xhr.statusText}`;
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { status: 'error', progress: 0, error }
            }));
            reject(new Error(error));
          }
        });

        xhr.addEventListener('error', () => {
          const error = 'Network error during upload';
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'error', progress: 0, error }
          }));
          reject(new Error(error));
        });

        xhr.timeout = 30 * 60 * 1000; // 30 minutes
        xhr.addEventListener('timeout', () => {
          const error = 'Upload timed out';
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'error', progress: 0, error }
          }));
          reject(new Error(error));
        });

        xhr.open('POST', `${getApiUrl()}/api/cdp/sessions/${session.session_id}/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });

    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      throw error;
    }
  };

  const uploadFilesInBatches = async (files) => {
    for (let i = 0; i < files.length; i += maxConcurrentUploads) {
      const batch = files.slice(i, i + maxConcurrentUploads);
      await Promise.all(batch.map(file => uploadSingleFile(file)));
    }
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    if (selectedFiles.length === 0) return;

    // Validate file sizes
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    const oversizedFiles = selectedFiles.filter(f => f.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      alert(`The following files exceed 2GB limit:\n${oversizedFiles.map(f => f.name).join('\n')}`);
      return;
    }

    const initialProgress = {};
    selectedFiles.forEach(file => {
      initialProgress[file.name] = { status: 'queued', progress: 0, error: null };
    });
    setUploadProgress(initialProgress);
    setUploadQueue(selectedFiles);

    try {
      setUploading(true);
      await uploadFilesInBatches(selectedFiles);
      onRefresh();
    } catch (error) {
      console.error('Error in upload process:', error);
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress({});
        setUploadQueue([]);
      }, 5000);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;

    try {
      const token = getCurrentToken();
      await apiCall(`/api/cdp/sessions/${session.session_id}/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const uploadDate = new Date(file.uploaded_at);
    const matchesDateFrom = !dateFilter.from || uploadDate >= new Date(dateFilter.from);
    const matchesDateTo = !dateFilter.to || uploadDate <= new Date(dateFilter.to);
    return matchesSearch && matchesDateFrom && matchesDateTo;
  });

  const filesByVersion = filteredFiles.reduce((acc, file) => {
    const version = file.version || 1;
    if (!acc[version]) acc[version] = [];
    acc[version].push(file);
    return acc;
  }, {});

  const versions = Object.keys(filesByVersion).sort((a, b) => b - a);

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Customer Data</h2>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-600 mb-4">
            Drag and drop your customer files here, or click to browse
          </p>
          <p className="text-xs text-gray-500 mb-4">
            Supported formats: CSV, XLS, XLSX (Max 2GB per file)
          </p>
          <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <Upload className="h-5 w-5 mr-2" />
            <span>{uploading ? 'Uploading...' : 'Choose Files'}</span>
            <input
              type="file"
              multiple
              accept=".csv,.xls,.xlsx"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="mt-6 space-y-3">
            <h3 className="font-semibold text-gray-900">Upload Progress</h3>
            {uploadQueue.map(file => {
              const progress = uploadProgress[file.name];
              if (!progress) return null;

              return (
                <div key={file.name} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700 truncate flex-1">
                      {file.name}
                    </span>
                    <span className="text-gray-500 ml-2">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          progress.status === 'completed' 
                            ? 'bg-green-500' 
                            : progress.status === 'error'
                            ? 'bg-red-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${progress.progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 w-12 text-right">
                      {progress.progress}%
                    </span>
                  </div>

                  {progress.status === 'completed' && (
                    <div className="flex items-center text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Upload complete
                    </div>
                  )}

                  {progress.status === 'error' && (
                    <div className="flex items-center text-xs text-red-600">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {progress.error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Upload Tips */}
        {uploading && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Uploading large files...</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Keep this page open until all uploads complete</li>
                  <li>Files are processed in batches of {maxConcurrentUploads}</li>
                  <li>Large files may take several minutes</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-b bg-gray-50">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
              className="w-full px-4 py-2 border rounded-lg text-sm"
              placeholder="From date"
            />
          </div>
          <div>
            <input
              type="date"
              value={dateFilter.to}
              onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
              className="w-full px-4 py-2 border rounded-lg text-sm"
              placeholder="To date"
            />
          </div>
        </div>
      </div>

      {filteredFiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <FileText className="h-16 w-16 text-gray-300 mb-4" />
          <p className="text-lg font-medium">No files uploaded</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Filename
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Columns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFiles.map((file) => (
                <tr key={file.file_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{file.filename}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                      v{file.version}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 uppercase">{file.file_type}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {(file.total_rows || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {file.total_columns || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {(file.file_size / 1024 / 1024).toFixed(2)} MB
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(file.uploaded_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => handleDeleteFile(file.file_id)}
                      disabled={session.columns_locked}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Data Unification Tab Component
const UnifyTab = ({ session, files, onRefresh }) => {
  const [columnMappings, setColumnMappings] = useState([]);
  const [deduplicationKeys, setDeduplicationKeys] = useState([]);
  const [indexableColumns, setIndexableColumns] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [columnAnalysis, setColumnAnalysis] = useState(null);
  const [showMappingEditor, setShowMappingEditor] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);

  useEffect(() => {
    if (files.length > 0) {
      analyzeColumns();
    }
  }, [files]);

  const analyzeColumns = async () => {
    try {
      setAnalyzing(true);
      const token = getCurrentToken();
      
      const response = await apiCall(`/api/cdp/sessions/${session.session_id}/analyze-columns`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const analysis = await response.json();
        setColumnAnalysis(analysis);
        
        // Initialize column mappings based on analysis
        initializeColumnMappingsFromAnalysis(analysis);
      }
    } catch (error) {
      console.error('Error analyzing columns:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const initializeColumnMappingsFromAnalysis = (analysis) => {
    const mappings = [];
    
    // Add exact matches
    if (analysis.exact_matches) {
      Object.entries(analysis.exact_matches).forEach(([colName, fileIds]) => {
        // Get the first file to determine data type
        const firstFileId = fileIds[0];
        const firstFile = files.find(f => f.file_id === firstFileId);
        const colMetadata = firstFile?.columns_metadata?.find(c => c.name === colName);
        
        fileIds.forEach(fileId => {
          mappings.push({
            source_file_id: fileId,
            source_column: colName,
            target_column: colName,
            data_type: colMetadata?.data_type || 'string',
            is_key: false,
            is_indexable: false,
            match_type: 'exact',
            confidence: 1.0
          });
        });
      });
    }
    
    // Add suggested fuzzy matches (user can accept/reject these)
    if (analysis.suggestions) {
      analysis.suggestions.forEach(suggestion => {
        const file = files.find(f => f.file_id === suggestion.source_file_id);
        const colMetadata = file?.columns_metadata?.find(c => c.name === suggestion.source_column);
        
        mappings.push({
          source_file_id: suggestion.source_file_id,
          source_column: suggestion.source_column,
          target_column: suggestion.target_column,
          data_type: colMetadata?.data_type || 'string',
          is_key: false,
          is_indexable: false,
          match_type: 'fuzzy',
          confidence: suggestion.confidence,
          suggested: true,
          accepted: false
        });
      });
    }
    
    // Add unique columns (unmapped)
    if (analysis.unique_columns) {
      Object.entries(analysis.unique_columns).forEach(([fileId, data]) => {
        const file = files.find(f => f.file_id === fileId);
        data.columns.forEach(colName => {
          const colMetadata = file?.columns_metadata?.find(c => c.name === colName);
          mappings.push({
            source_file_id: fileId,
            source_column: colName,
            target_column: colName, // Default to same name
            data_type: colMetadata?.data_type || 'string',
            is_key: false,
            is_indexable: false,
            match_type: 'unique',
            confidence: 1.0
          });
        });
      });
    }
    
    setColumnMappings(mappings);
  };

  const acceptSuggestedMapping = (index) => {
    const updated = [...columnMappings];
    updated[index].accepted = true;
    updated[index].suggested = false;
    setColumnMappings(updated);
  };

  const rejectSuggestedMapping = (index) => {
    const updated = [...columnMappings];
    const mapping = updated[index];
    
    // When rejecting a suggested mapping, DON'T change the target_column.
    // This keeps the row visible in the UI at its current location.
    // Instead, just mark it as rejected so the user knows it needs attention.
    // The user can then manually edit the target column if they want to change it.
    mapping.match_type = 'rejected';
    mapping.suggested = false;
    mapping.accepted = false;
    // Keep target_column as-is so the row doesn't disappear from the UI
    
    setColumnMappings(updated);
  };

  const updateColumnMapping = (index, field, value) => {
    const updated = [...columnMappings];
    updated[index][field] = value;
    setColumnMappings(updated);
  };

  const handleUnifyData = async () => {
    try {
      setProcessing(true);
      const token = getCurrentToken();

      // Filter out pending suggestions (not yet accepted/rejected) and process all others
      const finalMappings = columnMappings
        .filter(m => !m.suggested || m.accepted) // Exclude only pending suggestions
        .map(m => ({
          source_file_id: m.source_file_id,
          source_column: m.source_column,
          // If rejected, map to source column name; otherwise use the target
          target_column: m.match_type === 'rejected' ? m.source_column : m.target_column,
          data_type: m.data_type,
          is_key: m.is_key || false,
          is_indexable: m.is_indexable || false
        }));

      const request = {
        session_id: session.session_id,
        column_mappings: finalMappings,
        deduplication_keys: deduplicationKeys,
        create_indexes: indexableColumns
      };

      const response = await apiCall(`/api/cdp/sessions/${session.session_id}/unify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (response.ok) {
        alert('Data unification started! This may take a few minutes.');
        onRefresh();
      }
    } catch (error) {
      console.error('Error unifying data:', error);
      alert('Failed to start data unification');
    } finally {
      setProcessing(false);
    }
  };

  // Group mappings by target column for display
  const groupedMappings = columnMappings.reduce((acc, mapping, index) => {
    const key = mapping.target_column;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push({ ...mapping, originalIndex: index });
    return acc;
  }, {});

  const targetColumns = Object.keys(groupedMappings).sort();

  // Count pending suggestions
  const pendingSuggestions = columnMappings.filter(m => m.suggested && !m.accepted).length;
  
  // Count rejected mappings that need review
  const rejectedMappings = columnMappings.filter(m => m.match_type === 'rejected').length;

  return (
    <div className="space-y-6">

      {/* Show locked view if already unified */}
      {session?.status === 'ready' && session?.unified_at ? (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b bg-green-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Data Unified Successfully</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Unified on {new Date(session.unified_at).toLocaleString()} • {session.total_rows?.toLocaleString()} total rows
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            <h3 className="font-semibold text-gray-900 mb-4">Confirmed Column Mappings</h3>
            <p className="text-sm text-gray-600 mb-4">
              These column mappings were confirmed during unification and cannot be changed.
              To modify mappings, you'll need to create a new CDP session.
            </p>

            {/* Locked Column Mappings Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Target Column
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Source Columns
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Match Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Data Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Options
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {targetColumns.map((targetCol) => {
                    const mappings = groupedMappings[targetCol];
                    const firstMapping = mappings[0];
                    
                    return (
                      <tr key={targetCol} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {targetCol}
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            {mappings.map((mapping, idx) => {
                              const file = files.find(f => f.file_id === mapping.source_file_id);
                              return (
                                <div key={idx} className="flex items-center space-x-2 text-sm">
                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                  <span className="text-gray-600 text-xs">
                                    {file?.filename}: <span className="font-medium">{mapping.source_column}</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="space-y-1">
                            {mappings.map((mapping, idx) => (
                              <div key={idx}>
                                {mapping.match_type === 'exact' ? (
                                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Exact
                                  </span>
                                ) : mapping.match_type === 'fuzzy' ? (
                                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Fuzzy ({Math.round((mapping.confidence || 0) * 100)}%)
                                  </span>
                                ) : mapping.match_type === 'rejected' ? (
                                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Rejected - Mapped to Source
                                  </span>
                                ) : (
                                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Unique
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`font-medium ${getTypeColor(firstMapping.data_type)}`}>
                            {formatDataType(firstMapping.data_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3 text-xs">
                            {deduplicationKeys.includes(targetCol) && (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                Key
                              </span>
                            )}
                            {indexableColumns.includes(targetCol) && (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Indexed
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Mappings are locked</p>
                  <p>The column mappings shown above were used to unify your data and cannot be modified. 
                  You can now create segments and campaigns based on this unified dataset.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Editable unification panel (original) */
        <>
      {/* Column Analysis Warning */}
      {columnAnalysis && <ColumnMatchingWarning analysis={columnAnalysis} />}

      {/* Main Unification Panel */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Data Unification</h2>
              <p className="text-sm text-gray-600 mt-1">
                Review and adjust column mappings before unifying your data
              </p>
            </div>
            <button
              onClick={analyzeColumns}
              disabled={analyzing}
              className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${analyzing ? 'animate-spin' : ''}`} />
              <span>Re-analyze</span>
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-gray-600">Total Columns</p>
              <p className="text-xl font-bold text-gray-900">{targetColumns.length}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Dedup Keys</p>
              <p className="text-xl font-bold text-purple-600">{deduplicationKeys.length}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Exact Matches</p>
              <p className="text-xl font-bold text-green-600">
                {columnMappings.filter(m => m.match_type === 'exact').length}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Pending Suggestions</p>
              <p className="text-xl font-bold text-yellow-600">{pendingSuggestions}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Unique Columns</p>
              <p className="text-xl font-bold text-gray-600">
                {columnMappings.filter(m => m.match_type === 'unique').length}
              </p>
            </div>
          </div>
        </div>

        {/* Merge Strategy Info Box */}
        <div className="px-6 py-4 bg-blue-50 border-b hidden">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                Merge Strategy: {deduplicationKeys.length > 0 ? 'MERGE + DEDUPLICATE' : 'UNION ALL (Vertical Stack)'}
              </h3>
              {deduplicationKeys.length > 0 ? (
                <div className="text-xs text-blue-800 space-y-1">
                  <p>✓ Files will be <strong>merged horizontally</strong> and deduplicated on: <strong>{deduplicationKeys.join(', ')}</strong></p>
                  <p>✓ Multiple rows for the same key will be <strong>merged into one</strong></p>
                  <p>✓ Result: Unique records with combined attributes from all files</p>
                </div>
              ) : (
                <div className="text-xs text-blue-800 space-y-1">
                  <p>⚠ No deduplication keys selected - all rows from all files will be <strong>stacked vertically</strong></p>
                  <p>⚠ This may result in duplicate customer records across files</p>
                  <p>💡 <strong>Tip:</strong> Check the "Key" checkbox on columns like customer_id or email to enable deduplication</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pending Suggestions Section */}
        {pendingSuggestions > 0 && (
          <div className="px-6 py-4 bg-yellow-50 border-b">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-900">
                  {pendingSuggestions} Suggested Mapping{pendingSuggestions > 1 ? 's' : ''} Pending Review
                </h3>
                <p className="text-xs text-yellow-800 mt-1">
                  Review the fuzzy matches below and accept or reject each suggestion
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Column Mappings Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Target Column
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Source Mappings
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Match Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Data Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Options
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {targetColumns.map((targetCol) => {
                const mappings = groupedMappings[targetCol];
                const firstMapping = mappings[0];
                const hasSuggestions = mappings.some(m => m.suggested);
                const hasRejected = mappings.some(m => m.match_type === 'rejected');
                
                return (
                  <tr key={targetCol} className={`hover:bg-gray-50 ${
                    hasSuggestions ? 'bg-yellow-50' : hasRejected ? 'bg-red-50' : ''
                  }`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <div className="flex items-center space-x-2">
                        <span>{targetCol}</span>
                        {hasSuggestions && (
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                        )}
                        {hasRejected && (
                          <X className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="space-y-1">
                        {mappings.map((mapping, idx) => {
                          const file = files.find(f => f.file_id === mapping.source_file_id);
                          return (
                            <div key={idx} className="flex items-center space-x-2">
                              {mapping.suggested ? (
                                <AlertTriangle className="h-3 w-3 text-yellow-500" />
                              ) : mapping.match_type === 'rejected' ? (
                                <X className="h-3 w-3 text-red-500" />
                              ) : mapping.match_type === 'exact' ? (
                                <CheckCircle className="h-3 w-3 text-green-500" />
                              ) : (
                                <Info className="h-3 w-3 text-gray-400" />
                              )}
                              <span className="text-gray-600 text-xs">
                                {file?.filename}: <span className="font-medium">{mapping.source_column}</span>
                              </span>
                              {mapping.confidence < 1.0 && (
                                <span className="text-xs text-gray-500">
                                  ({Math.round(mapping.confidence * 100)}%)
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="space-y-1">
                        {mappings.map((mapping, idx) => (
                          <div key={idx}>
                            {mapping.suggested ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Suggested
                              </span>
                            ) : mapping.match_type === 'exact' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Exact
                              </span>
                            ) : mapping.match_type === 'fuzzy' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Fuzzy
                              </span>
                            ) : mapping.match_type === 'rejected' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Rejected - Review Needed
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Unique
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`font-medium ${getTypeColor(firstMapping.data_type)}`}>
                        {formatDataType(firstMapping.data_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <label className="flex items-center space-x-1 text-xs">
                          <input
                            type="checkbox"
                            checked={deduplicationKeys.includes(targetCol)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDeduplicationKeys([...deduplicationKeys, targetCol]);
                              } else {
                                setDeduplicationKeys(deduplicationKeys.filter(k => k !== targetCol));
                              }
                            }}
                            className="rounded text-blue-600"
                          />
                          <span>Key</span>
                        </label>
                        <label className="flex items-center space-x-1 text-xs">
                          <input
                            type="checkbox"
                            checked={indexableColumns.includes(targetCol)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setIndexableColumns([...indexableColumns, targetCol]);
                              } else {
                                setIndexableColumns(indexableColumns.filter(k => k !== targetCol));
                              }
                            }}
                            className="rounded text-blue-600"
                          />
                          <span>Index</span>
                        </label>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        {mappings.some(m => m.suggested) && (
                          <>
                            <button
                              onClick={() => {
                                mappings.forEach(m => {
                                  if (m.suggested) {
                                    acceptSuggestedMapping(m.originalIndex);
                                  }
                                });
                              }}
                              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Accept all suggestions for this column"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                mappings.forEach(m => {
                                  if (m.suggested) {
                                    rejectSuggestedMapping(m.originalIndex);
                                  }
                                });
                              }}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Reject all suggestions for this column"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditingMapping(targetCol)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit mapping"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {pendingSuggestions > 0 ? (
                <span className="text-yellow-700 font-medium">
                  ⚠️ Please review {pendingSuggestions} pending suggestion{pendingSuggestions > 1 ? 's' : ''} before unifying
                </span>
              ) : rejectedMappings > 0 ? (
                <span className="text-blue-700 font-medium">
                  ℹ️ {rejectedMappings} rejected mapping{rejectedMappings > 1 ? 's' : ''} will map to their source column names
                </span>
              ) : (
                <span className="text-green-700 font-medium">
                  ✓ All mappings reviewed - ready to unify
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  // Accept all suggestions
                  columnMappings.forEach((m, idx) => {
                    if (m.suggested) {
                      acceptSuggestedMapping(idx);
                    }
                  });
                }}
                disabled={pendingSuggestions === 0}
                className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Accept All Suggestions
              </button>
              <button
                onClick={handleUnifyData}
                disabled={processing || pendingSuggestions > 0 || columnMappings.length === 0}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="h-5 w-5" />
                <span>{processing ? 'Processing...' : 'Unify Data'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mapping Editor Modal */}
      {editingMapping && (
        <MappingEditorModal
          targetColumn={editingMapping}
          mappings={groupedMappings[editingMapping]}
          files={files}
          allTargetColumns={targetColumns}
          onClose={() => setEditingMapping(null)}
          onSave={(updatedMappings) => {
            const newMappings = [...columnMappings];
            updatedMappings.forEach(updated => {
              newMappings[updated.originalIndex] = updated;
            });
            setColumnMappings(newMappings);
            setEditingMapping(null);
          }}
        />
      )}
      </>
      )}
    </div>
  );
};

// Segments Tab Component
const SegmentsTab = ({ session, segments, onRefresh, onSwitchToQueryExplorer }) => {
  const [viewingSegment, setViewingSegment] = useState(null);

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm('Are you sure you want to delete this segment?')) {
      return;
    }

    try {
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/segments/${segmentId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        onRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete segment');
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      alert('Failed to delete segment');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Customer Segments</h2>
          <p className="text-sm text-gray-600 mt-1">Saved segments from the Query Explorer</p>
        </div>
        <button
          onClick={onSwitchToQueryExplorer}
          disabled={session?.status !== 'ready'}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Plus className="h-5 w-5" />
          <span>Create Segment</span>
        </button>
      </div>

      {segments.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Filter className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900">No segments created yet</p>
          <p className="text-sm text-gray-600 mt-1">
            Use the <button onClick={onSwitchToQueryExplorer} className="text-blue-600 hover:underline font-medium">Query Explorer</button> to build a filter, then save it as a segment
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {segments.map((segment) => (
            <div key={segment.segment_id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{segment.segment_name}</h3>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {segment.customer_count?.toLocaleString()} customers
                </span>
              </div>
              {segment.description && (
                <p className="text-sm text-gray-600 mb-4">{segment.description}</p>
              )}
              {segment.conditions && segment.conditions.length > 0 && (
                <div className="mb-4 space-y-1">
                  {segment.conditions.slice(0, 3).map((cond, idx) => (
                    <div key={idx} className="text-xs font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded">
                      {idx > 0 && <span className="font-bold text-gray-700">{cond.logic_operator} </span>}
                      {cond.column} {cond.operator} {JSON.stringify(cond.value)}
                    </div>
                  ))}
                  {segment.conditions.length > 3 && (
                    <span className="text-xs text-gray-400">+{segment.conditions.length - 3} more conditions</span>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-xs text-gray-500">
                  Created {new Date(segment.created_at).toLocaleDateString()}
                </span>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setViewingSegment(segment)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
                    title="View & Filter"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => handleDeleteSegment(segment.segment_id)}
                    className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center space-x-1"
                    title="Delete Segment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewingSegment && (
        <SegmentDetailsModal
          session={session}
          segment={viewingSegment}
          onClose={() => setViewingSegment(null)}
        />
      )}
    </div>
  );
};

// Segment Details Modal Component
const SegmentDetailsModal = ({ session, segment, onClose }) => {
  const [segmentData, setSegmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState('browse'); // 'browse' or 'filter'

  useEffect(() => {
    loadSegmentData();
  }, [segment.segment_id, currentPage, pageSize]);

  const loadSegmentData = async () => {
    try {
      setLoading(true);
      const token = getCurrentToken();
      
      const response = await apiCall(`/api/cdp/sessions/${session.session_id}/segments/${segment.segment_id}/data?page=${currentPage}&limit=${pageSize}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSegmentData(data);
        setTotalCount(data.total_count || data.filtered_count || data.count || 0);
      }
    } catch (error) {
      console.error('Error loading segment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  // System columns to hide from display
  const systemColumns = [
    '_customer_key',
    '_version',
    '_version_type',
    '_source_campaign_id',
    '_source_file_id',
    '_created_at',
    '_updated_at',
    '_tenant_id',
    '_is_new',
    '_is_updated',
    '_changed_fields'
  ];

  const filterSystemColumns = (columns) => {
    return columns.filter(col => !systemColumns.includes(col));
  };

  const getVisibleColumns = () => {
    if (segmentData?.sample && segmentData.sample.length > 0) {
      const allColumns = Object.keys(segmentData.sample[0]);
      return filterSystemColumns(allColumns);
    }
    return [];
  };

  const visibleColumns = getVisibleColumns();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{segment.segment_name}</h2>
              <p className="text-sm text-gray-600 mt-1">{segment.description}</p>
            </div>
            <div className="flex items-center space-x-3">
              {/* View mode toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('browse')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    viewMode === 'browse' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  Browse
                </button>
                <button
                  onClick={() => setViewMode('filter')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    viewMode === 'filter' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  Filter & Query
                </button>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {viewMode === 'filter' ? (
            /* Query Explorer embedded in segment context */
            <QueryExplorer
              session={session}
              segmentId={segment.segment_id}
              segmentName={segment.segment_name}
            />
          ) : (
            /* Original browse view */
            <>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : segmentData ? (
                <div className="space-y-6">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-600 font-medium">Total Customers</p>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {totalCount.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-600 font-medium">Conditions</p>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {segmentData.conditions?.length || 0}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-sm text-purple-600 font-medium">Created</p>
                      <p className="text-sm font-medium text-purple-900 mt-2">
                        {new Date(segment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Sample Data */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Customers (Page {currentPage + 1} of {totalPages})
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Rows per page:</span>
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(0);
                          }}
                          className="px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>
                    {segmentData.sample && segmentData.sample.length > 0 ? (
                      <div className="space-y-3">
                        <div className="border rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  {visibleColumns.map((key) => (
                                    <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      {key}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {segmentData.sample.map((row, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    {visibleColumns.map((key) => (
                                      <td key={key} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                        {row[key] == null ? (
                                          <span className="text-gray-300 italic text-xs">null</span>
                                        ) : String(row[key])}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        
                        {/* Pagination Controls */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border rounded-lg">
                          <div className="text-sm text-gray-700">
                            Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                            <span className="font-medium">{Math.min((currentPage + 1) * pageSize, totalCount)}</span> of{' '}
                            <span className="font-medium">{totalCount.toLocaleString()}</span> results
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setCurrentPage(0)}
                              disabled={!hasPrevPage}
                              className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              First
                            </button>
                            <button
                              onClick={() => setCurrentPage(prev => prev - 1)}
                              disabled={!hasPrevPage}
                              className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1 text-sm text-gray-700">
                              Page {currentPage + 1} of {totalPages}
                            </span>
                            <button
                              onClick={() => setCurrentPage(prev => prev + 1)}
                              disabled={!hasNextPage}
                              className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Next
                            </button>
                            <button
                              onClick={() => setCurrentPage(totalPages - 1)}
                              disabled={!hasNextPage}
                              className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center py-8 text-gray-500">No customer data available</p>
                    )}
                  </div>

                  {/* Export Button */}
                  <div className="flex justify-end">
                    <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                      <Download className="h-4 w-4" />
                      <span>Export Segment</span>
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-center py-8 text-gray-500">Failed to load segment data</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'draft': return 'bg-gray-100 text-gray-800';
    case 'processing': return 'bg-blue-100 text-blue-800';
    case 'ready': return 'bg-green-100 text-green-800';
    case 'active': return 'bg-blue-100 text-blue-800';
    case 'completed': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export default CDPSessionView;